// Minnesotaänger Mini App — v1 conversation/history actions.
// New code (not from telegram-tt). Contract with the backend:
// minnesanger-miniapp-v1/03-api-draft.md (GET /conversations,
// GET /conversations/{chat_id}/messages with before_event_id/limit).

import type { ActionReturnType } from '../../types';
import { ApiMiniAppError, miniappApi } from '../../../api/miniapp/client';
import { addActionHandler, getActions, getGlobal, setGlobal } from '../../index';
import { setBootStage } from '../../../util/bootStage';
import { updateMiniapp } from '../../miniapp/update';

const PAGE_LIMIT = 50;

export function getErrorMessage(err: unknown): string {
  if (err instanceof ApiMiniAppError) {
    // Test-phase visibility: INTERNAL carries the HTTP status or network error
    // in its message already; server codes (401/403/404/...) get prefixed so
    // the failure reason is diagnosable from the UI.
    const code = err.code && err.code !== 'INTERNAL' ? `[${err.code}] ` : '';
    const detail = err.detail ? ` (${JSON.stringify(err.detail)})` : '';
    return `${code}${err.message}${detail}`;
  }
  return '网络请求失败，请稍后重试';
}

function withoutItem<T>(items: T[], item: T): T[] {
  return items.filter((x) => x !== item);
}

function omitKey<K extends string, V>(record: Record<K, V>, key: K): Record<K, V> {
  const { [key]: _removed, ...rest } = record;
  return rest as Record<K, V>;
}

// Called from index.tsx after initGlobal. Nothing to do in v1 — the global
// tree is already fresh (no cache restore, no session, no passcode).
addActionHandler('init', (): ActionReturnType => undefined);
addActionHandler('reset', (): ActionReturnType => undefined);

// v1 has no push channel; the backend does not emit updates. Kept as a no-op
// so callers (if any) never crash on an unregistered action.
addActionHandler('apiUpdate', (): ActionReturnType => undefined);

addActionHandler('loadConversations', async (global): Promise<void> => {
  if (global.miniapp.isLoadingConversations) return;

  // v1: boot diagnostics — 会话数据链阶段（见 public/boot-diag.js）。
  setBootStage('conversations-loading');

  setGlobal(updateMiniapp(global, (m) => ({
    ...m,
    isLoadingConversations: true,
    // Clear a previous failure when a (re)load starts; "会话加载失败" must not
    // linger while a retry is in flight.
    conversationsError: null,
  })));

  try {
    const { conversations } = await miniappApi.getConversations();
    global = getGlobal();
    setGlobal(updateMiniapp(global, (m) => ({
      ...m,
      conversations,
      isLoadingConversations: false,
      conversationsError: null,
    })));
    setBootStage('conversations-loaded', conversations.length);
    setBootStage('ready');
  } catch (err) {
    // The failure is rendered inline in LeftColumn (with a retry action).
    // showDialog is not used here: the v1 trimmed Main renders no Dialog
    // container, so a dialog-only error would be invisible — exactly how the
    // previous "暂无会话" masquerade happened.
    const code = err instanceof ApiMiniAppError ? (err.code || 'unknown') : 'network';
    setBootStage('conversations-error:' + code);
    setBootStage('ready');
    global = getGlobal();
    setGlobal(updateMiniapp(global, (m) => ({
      ...m,
      isLoadingConversations: false,
      conversationsError: getErrorMessage(err),
    })));
  }
});

addActionHandler('openChat', (global, actions, { chatId }): ActionReturnType => {
  if (global.miniapp.activeChatId === chatId) {
    return undefined;
  }

  setGlobal(updateMiniapp(global, (m) => ({
    ...m,
    activeChatId: chatId,
    // Entering a chat resets its transient UI state.
    selectedMessageIdsByChatId: omitKey(m.selectedMessageIdsByChatId, chatId),
    replyingToEventIdByChatId: omitKey(m.replyingToEventIdByChatId, chatId),
    sendFailedByChatId: { ...m.sendFailedByChatId, [chatId]: null },
  })));

  actions.loadHistory({ chatId });
});

addActionHandler('closeChat', (global): ActionReturnType => {
  const { activeChatId } = global.miniapp;
  if (!activeChatId) return undefined;

  return updateMiniapp(global, (m) => ({
    ...m,
    activeChatId: undefined,
    replyingToEventIdByChatId: omitKey(m.replyingToEventIdByChatId, activeChatId),
    selectedMessageIdsByChatId: omitKey(m.selectedMessageIdsByChatId, activeChatId),
    sendFailedByChatId: omitKey(m.sendFailedByChatId, activeChatId),
  }));
});

addActionHandler('openPreviousChat', (global): ActionReturnType => {
  // v1 has no chat navigation stack; Esc/back just closes the open chat.
  if (!global.miniapp.activeChatId) return undefined;
  const { activeChatId } = global.miniapp;
  return updateMiniapp(global, (m) => ({
    ...m,
    activeChatId: undefined,
    replyingToEventIdByChatId: omitKey(m.replyingToEventIdByChatId, activeChatId),
    selectedMessageIdsByChatId: omitKey(m.selectedMessageIdsByChatId, activeChatId),
    sendFailedByChatId: omitKey(m.sendFailedByChatId, activeChatId),
  }));
});

// Always re-fetches and replaces the chat's message list (idempotent GET).
// The UI only dispatches this when needed (chat open, post-send refresh,
// post-reroll/edit/delete refresh), so duplicates only cost a fetch.
addActionHandler('loadHistory', async (global, actions, { chatId }): Promise<void> => {
  if (global.miniapp.isLoadingMessagesChatIds.includes(chatId)) return;

  setGlobal(updateMiniapp(global, (m) => ({
    ...m,
    isLoadingMessagesChatIds: [...m.isLoadingMessagesChatIds, chatId],
  })));

  try {
    const { messages, next_before_event_id } = await miniappApi.getMessages(chatId, undefined, PAGE_LIMIT);
    global = getGlobal();
    setGlobal(updateMiniapp(global, (m) => ({
      ...m,
      messagesByChatId: { ...m.messagesByChatId, [chatId]: messages },
      olderCursorByChatId: { ...m.olderCursorByChatId, [chatId]: next_before_event_id },
      isLoadingMessagesChatIds: withoutItem(m.isLoadingMessagesChatIds, chatId),
      sendFailedByChatId: { ...m.sendFailedByChatId, [chatId]: null },
    })));
  } catch (err) {
    global = getGlobal();
    setGlobal(updateMiniapp(global, (m) => ({
      ...m,
      isLoadingMessagesChatIds: withoutItem(m.isLoadingMessagesChatIds, chatId),
    })));
    actions.showDialog({ title: '加载消息失败', message: getErrorMessage(err) });
  }
});

addActionHandler('loadMoreHistory', async (global, actions, { chatId }): Promise<void> => {
  const { olderCursorByChatId, isLoadingMoreChatIds } = global.miniapp;
  const beforeEventId = olderCursorByChatId[chatId];
  if (beforeEventId === null || beforeEventId === undefined) return;
  if (isLoadingMoreChatIds.includes(chatId)) return;

  setGlobal(updateMiniapp(global, (m) => ({
    ...m,
    isLoadingMoreChatIds: [...m.isLoadingMoreChatIds, chatId],
  })));

  try {
    const { messages, next_before_event_id } = await miniappApi.getMessages(chatId, beforeEventId, PAGE_LIMIT);
    global = getGlobal();
    setGlobal(updateMiniapp(global, (m) => {
      const existing = m.messagesByChatId[chatId] ?? [];
      // Newest-last order: the older page is prepended.
      return {
        ...m,
        messagesByChatId: { ...m.messagesByChatId, [chatId]: [...messages, ...existing] },
        olderCursorByChatId: { ...m.olderCursorByChatId, [chatId]: next_before_event_id },
        isLoadingMoreChatIds: withoutItem(m.isLoadingMoreChatIds, chatId),
      };
    }));
  } catch (err) {
    global = getGlobal();
    setGlobal(updateMiniapp(global, (m) => ({
      ...m,
      isLoadingMoreChatIds: withoutItem(m.isLoadingMoreChatIds, chatId),
    })));
    actions.showDialog({ title: '加载更多失败', message: getErrorMessage(err) });
  }
});
